import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Inject, OnInit, Optional } from '@angular/core';
import { Pagination, Swiper } from 'swiper';
import { DATA_INJECTION_TOKEN } from '../hero-section.component';
import { Photo, PhotoResultSet, PropertyDetails } from 'src/app/services/portal-api.service';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { combineLatest } from 'rxjs';

@Component({
  selector: 'app-hero-image-swiper',
  standalone: true,
  imports: [CommonModule, TranslocoDirective],
  templateUrl: './hero-image-swiper.component.html',
  styleUrl: './hero-image-swiper.component.css'
})
export class HeroImageSwiperComponent implements OnInit, AfterViewInit {
  private swiper!: Swiper;
  public currentIndex = 0;
  public totalSlides: number = 0;
  public hasApprovalBanner: boolean = false;
  protected activeLang: string = 'el';

  constructor(
    @Inject(DATA_INJECTION_TOKEN) @Optional() public readonly data: { photos: PhotoResultSet, model: PropertyDetails, floorPlanExists: boolean, combinedArray: (PhotoResultSet | PropertyDetails)[] },
    private translocoService: TranslocoService
  ) {

  }

  ngOnInit() {
    this.translocoService.langChanges$.subscribe(lang => {
      this.activeLang = lang;
    })
  }

  ngAfterViewInit() {
    this.prepareSlides();
    this.initializeSwiper();
  }

  prepareSlides() {
    if (this.data.photos?.items) {
      // Clean the array first
      this.data.photos.items = this.data.photos.items
        .filter(photo => !!photo && !!photo.uri) // Remove empty/null items
        .filter(photo => !photo.isDefault) // Remove any existing default photos
        .map((photo: Photo) => {
          if (!Array.isArray(photo.photoItems)) {
            return photo;
          }

          let webpUri = photo.photoItems.find(f => f?.uri?.endsWith('-l.webp'))?.uri;

          photo.uri = webpUri || photo.uri || '';

          return photo;
        });
  
      // Add default photo if it exists and has a URI
      if (this.data.model.defaultPhoto?.uri) {
        this.data.photos.items.unshift(this.data.model.defaultPhoto);
      }
  
      this.totalSlides = this.data.photos.items.length;
    }
  }

  isPreapproved() {
    return this.data.model.preApproved === 'Approved' && !this.data.model.isSold;
  }

  getRealSlideCount(): number {
    if (!this.swiper) return this.data.photos.items?.length || 0;
    
    // When loop is enabled, subtract the duplicated slides
    return this.swiper.loopedSlides 
      ? this.swiper.slides.length - (this.swiper.loopedSlides * 2)
      : this.swiper.slides.length;
  }

  initializeSwiper(): void {
    if (this.swiper) {
      this.swiper.destroy();
    }

    this.swiper = new Swiper('.heroSwiper', {
      direction: 'horizontal',
      modules: [Pagination],
      slidesPerView: 1,
      centeredSlides: true,
      loop: this.data.photos.items && this.data.photos.items.length > 2,
      pagination: {
        el: '.custom-swiper-pagination',
        clickable: true,
        renderBullet: function (index, className) {
          return '<span class="' + className + '"></span>';
        }
      },
      observer: true,
      observeParents: true,
      mousewheel: {
        releaseOnEdges: true,
      },
      touchReleaseOnEdges: true,
      on: {
        init: () => {
          document.querySelectorAll('.swiper-slide:empty').forEach(el => el.remove());
          this.swiper?.update();
          this.currentIndex = this.swiper?.realIndex ?? 0; // Initialize with realIndex
        },
        slideChange: () => {
          if (this.swiper && this.swiper.loopedSlides) {
            this.currentIndex = this.swiper.realIndex; // Always use realIndex
            this.totalSlides = this.swiper.slides.length - (this.swiper.loopedSlides * 2); // Calculate real slides count
          }
        },
        transitionEnd: () => {
          // Additional sync after animation completes
          if (this.swiper) {
            this.currentIndex = this.swiper.realIndex;
          }
        }
      }
    });
  }
}
